import React, { LegacyRef, createRef, useEffect, useRef, useState, useLayoutEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { RouteComponentProps } from "react-router"
import { mdiFilter, mdiFilterOutline, mdiRefresh, mdiUpload } from "@mdi/js"
import { cloneDeep } from "lodash"
import Radium from "radium"
import Modal from "../../components/Modals/Modal"
import { Table } from "simplygrow-ui"

import { ColorPalette } from "../../config/colors"
import { ActionType } from "../../store/actions/actions"
import {
  addUnderScores,
  jsonToCsv,
  normaliseStringForComparison,
  packageDataAndDownloadFile,
  titleCase,
  toLowerCaseCustom,
  toUpperCaseCustom,
} from "../../utils"
import { DataCaptureMode, DocumentCategories } from "../../config"
import {
  EmploymentStatus,
  UiCategoryEnum,
  TrashStatus,
  AssociationId,
  ColumnConfig,
  NavMenuAccess,
  FileFormat,
  ConfigTypeEnum,
  ProfilePk,
  Username,
  IntegrationWarnings,
} from "../../types"
import completedScreens from "../completedScreens"
import { ProcessModes } from "../../config/processModes"
import { CustomFilterState } from "../../components/Tables/DataTable/DataTable"
import NavBar from "../../components/Navigation/NavBar"
import SectionHeader from "../../components/Headings/SectionHeaderPrimary"
import LoadingModal from "../../components/Modals/LoadingModal"
import AlertModalOneButton from "../../components/Modals/AlertModalOneButton"
import SideMenu from "../../components/Navigation/SideMenu"
import { ProfileNavMenu } from "../../components/GeneralUI/ProfileNavMenu/ProfileNavMenu"
import { PeopleFlowCombinedReducer } from "../../store"
import { SessionService, ProfileService, ConfigService, TableService, FilterService } from "../../services"
import { ActionButtonType, Toolbar } from "../../components/GeneralUI/Toolbar"
import { AssociationSettingsRepository, ProfileRepository, UsersRepository } from "../../repositories"
import { Profile } from "../../models/profile/profile"
import {
  GeneralErrorCodesEnum,
  IamServiceErrorCodesEnum,
  ConfigServiceErrorCodesEnum,
  CommonUnixMsEnum,
} from "../../enums"
import FieldFilter from "../../components/GeneralUI/FieldFilter/FieldFilter"
import FileSelectionModal from "../../components/Modals/FileSelectionModal"
import { ProfileIntegrationWarningModal } from "../../components/Modals/ProfileIntegrationWarningsModal/ProfileIntegrationWarningsModal"
import { IntegrationsService } from "../../services/integrationsService"

const { MissingProfile } = GeneralErrorCodesEnum
const { Unauthorised } = IamServiceErrorCodesEnum
const { InvalidFileName, InvalidConfigType } = ConfigServiceErrorCodesEnum

const sessionService = new SessionService()
const configService = new ConfigService()
const profileService = new ProfileService()
const tableService = new TableService()
const filterService = new FilterService()
const integrationsService = new IntegrationsService()

interface ProfilesProps extends RouteComponentProps {}

type ProfileCore = { id: string; name: string; surname: string; idPassport: string }
type RouteState = {
  prevFilterState: CustomFilterState[]
}
const profileViewingOptionsRef: LegacyRef<ProfileNavMenu> = createRef()
const sectionHeaderRef: LegacyRef<any> = createRef()
const primaryTableRef: LegacyRef<HTMLDivElement> = createRef()

const Profiles = (props: ProfilesProps) => {
  const initialModalState = {
    loadingModalOpen: false,
    accountModalOpen: false,
    forgotPasswordModalOpen: false,
    settingsModalOpen: false,
    warningModalOpen: false,
    loadingModalMessage: "",
    loadingModalAuxMessage: "",
    warningModalHeader: "",
    warningModalMessage: "",
    configUploadModalOpen: false,
    profileIntegrationWarningModalOpen: false,
  }

  const [modalState, setModalState] = useState(initialModalState)
  const [sideMenuComponents, setSideMenuComponents] = useState(<div />)
  const [sideMenuVisible, setSideMenuVisible] = useState(false)
  const [profileData, setProfileData] = useState<any[]>([])
  const [searchString, setSearchString] = useState("")
  const [filterConfig, setFilterConfig] = useState<{ key: string; label: string }[]>([
    {
      key: "idPassport",
      label: "ID/Passport",
    },
  ])
  const [tableHeight, setTableHeight] = useState<number>(500)
  const [tableReloadTrigger, setTableReloadTrigger] = useState(0)
  const [selectedProfilePks, setSelectedProfilePks] = useState<string[]>([])
  const [tableColumnConfig, setTableColumnConfig] = useState<ColumnConfig[]>([])
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filters, setFilters] = useState<CustomFilterState[]>([])
  const [integrationWarnings, setIntegrationWarnings] = useState<IntegrationWarnings[]>([])
  const integrationWarningsTimer = useRef<ReturnType<typeof setTimeout>>()

  const userIdPassport = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.idPassport)
  const password = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.password)
  const associationRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.associationRepo,
  ) as AssociationSettingsRepository
  const profileRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.profileRepo,
  ) as ProfileRepository
  const userRepo = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.userRepo) as UsersRepository
  const selectedAssociation = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedAssociation,
  )
  const selectedCohort = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.selectedCohort)
  const selectedEmploymentStatus = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedEmploymentStatus,
  )
  const currentScreen = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.currentScreen)
  const navMenuAccess = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.navMenuAccess)
  const lastProfileSaveUnixMs = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.lastProfileSaveUnixMs,
  )

  // const associationsWithInitialProfileLoad = useSelector(
  //   (state: PeopleFlowCombinedReducer) => state.sessionManager.associationsWithInitialProfileLoad,
  // )

  const dispatch = useDispatch()
  const updateState = (data: any) => dispatch({ type: ActionType.UPDATE_STATE, data })
  const recordAssociationInitialProfileLoad = (association: AssociationId) =>
    dispatch({ type: ActionType.RECORD_ASSOCIATION_INITIAL_PROFILE_LOAD, data: { association } })

  useEffect(() => {
    if (!selectedAssociation) {
      return
    }
    const initialFilters = (props.location.state as RouteState)?.prevFilterState
    const tableColumnConfig = initialiseTableColumnConfig(associationRepo, selectedAssociation)
    const filterConfig = associationRepo.getProfileFilterConfig(selectedAssociation)

    setFilterConfig(filterConfig)
    setTableColumnConfig(tableColumnConfig)
    setFilters(initialFilters || [])
    setSideMenuVisible(false)
    updateState({
      profilePic: "",
      currentSection: UiCategoryEnum.PEOPLE,
      currentScreen: `${selectedEmploymentStatus}S`,
    })
    updateProfileRenderData(tableColumnConfig, filterConfig, initialFilters)
    scheduleIntegrationWarningsFetch(selectedAssociation, userIdPassport, password, lastProfileSaveUnixMs)
    return () => {
      if (integrationWarningsTimer.current) {
        clearTimeout(integrationWarningsTimer.current)
      }
    }
  }, [selectedAssociation, selectedCohort])

  useLayoutEffect(() => {
    if (!primaryTableRef.current) {
      return
    }
    const tableTopY = primaryTableRef.current.getBoundingClientRect().top
    const browserWindowHeight = window.innerHeight
    setTableHeight(browserWindowHeight - tableTopY)
  }, [primaryTableRef.current])

  const closeModals = () => {
    setModalState(initialModalState)
  }

  const initialiseTableColumnConfig = (associationRepo: AssociationSettingsRepository, association: AssociationId) => {
    const columnConfig = associationRepo.getProfileTableColumnConfig(association)
    const clonedColumnConfig = cloneDeep(columnConfig)
    return clonedColumnConfig.slice(0, 5)
  }

  // A save takes time to propagate through the integrations pipeline, so warnings fetched
  // immediately after one are stale and re-flag the profile the user has just fixed.
  const scheduleIntegrationWarningsFetch = (
    association: AssociationId,
    username: Username,
    password: string,
    lastProfileSaveUnixMs: number,
  ) => {
    const msSinceLastProfileSave = Date.now() - lastProfileSaveUnixMs
    const delayMs = Math.max(0, CommonUnixMsEnum.EIGHT_SECONDS - msSinceLastProfileSave)
    if (!delayMs) {
      fetchIntegrationWarnings(association, username, password)
      return
    }
    integrationWarningsTimer.current = setTimeout(
      () => fetchIntegrationWarnings(association, username, password),
      delayMs,
    )
  }

  const fetchIntegrationWarnings = async (association: AssociationId, username: Username, password: string) => {
    const authCredentials = { username, password }
    const integrationWarnings = await integrationsService.fetchIntegrationWarnings(association, authCredentials)
    if (!integrationWarnings.length) {
      return
    }
    setIntegrationWarnings(integrationWarnings)
    // The fetch is now delayed, so by the time it lands the user may have opened something else.
    // Opening this modal resets the rest of the modal state, so yield rather than close their dialog.
    setModalState(current => {
      const anotherModalOpen =
        current.loadingModalOpen || current.warningModalOpen || current.configUploadModalOpen
      if (anotherModalOpen) {
        return current
      }
      return {
        ...initialModalState,
        profileIntegrationWarningModalOpen: true,
      }
    })
  }

  const markIntegrationWarningResolved = async (
    selectedProfileIds: ProfilePk[],
    association: AssociationId,
    username: Username,
    password: string,
  ) => {
    try {
      if (!selectedProfileIds.length) {
        setModalState({
          ...modalState,
          warningModalOpen: true,
          warningModalHeader: "No items selected",
          warningModalMessage: "Select at least one item to mark as resolved.",
        })
        return
      }
      setModalState({
        ...initialModalState,
        loadingModalOpen: true,
        loadingModalMessage: "Marking resolved items",
      })
      const authCredentials = { username, password }
      const integrationWarnings = await integrationsService.markIntegrationWarningResolved(
        selectedProfileIds,
        association,
        authCredentials,
      )
      setIntegrationWarnings(integrationWarnings)
      setModalState({
        ...initialModalState,
        profileIntegrationWarningModalOpen: true,
      })
    } catch (error) {
      console.error(error)
    }
  }

  const downloadAssociationProfiles = async (
    isFreshDownload: boolean,
    tableColumnConfig: ColumnConfig[],
    profileFilterConfig: { key: string; label: string }[],
    profileFilters: CustomFilterState[],
  ) => {
    try {
      const loadingModalMessage = isFreshDownload ? "Refreshing profiles" : "Checking for updates..."
      setModalState({
        ...initialModalState,
        loadingModalOpen: true,
        loadingModalMessage,
        loadingModalAuxMessage: "This may take a few minutes",
      })

      await profileService.downloadAssociationProfiles(
        isFreshDownload,
        sessionService,
        associationRepo,
        profileRepo,
        { username: userIdPassport, password },
        selectedAssociation,
        selectedCohort,
        (progress: string) => {
          setModalState({
            ...initialModalState,
            loadingModalOpen: true,
            loadingModalMessage,
            loadingModalAuxMessage: progress,
          })
        },
      )
      recordAssociationInitialProfileLoad(selectedAssociation)
      updateProfileRenderData(tableColumnConfig, profileFilterConfig, profileFilters)
    } catch (error: any) {
      errorHandler(error)
    }
    closeModals()
  }

  const updateProfileRenderData = (
    tableColumnConfig: ColumnConfig[],
    filterConfig: { key: string; label: string }[],
    profileFilters?: CustomFilterState[],
  ) => {
    const prepareProfileDataForDisplay = (
      profilesEntitiesToRender: Profile[],
      tableColumnConfig: ColumnConfig[],
      filterConfig: { key: string; label: string }[],
    ): any[] => {
      return profilesEntitiesToRender.map((profileEntity: any) => {
        const profilePk = profileEntity.getPk()
        let returnData = { id: profilePk } as Record<string, string>
        filterConfig.forEach((configItem) => {
          returnData[configItem.key] = profileEntity.getGeneralDataValue(configItem.key, "-")
        })
        tableColumnConfig.forEach((configItem) => {
          let value = profileEntity.getGeneralDataValue(configItem.key, "-")
          if (Array.isArray(value)) {
            value = value.join("; ")
          }
          returnData[configItem.key] = toUpperCaseCustom(value)
        })
        return returnData
      })
    }

    const allAssociationProfileEntities = profileRepo.getAllProfileEntities()
    if (!allAssociationProfileEntities) {
      return
    }
    const allProfilesToRender = profileRepo.getTargetProfiles(
      [selectedCohort],
      [selectedEmploymentStatus],
      [TrashStatus.NONE],
    )
    let updatedProfileData = prepareProfileDataForDisplay(
      Object.values(allProfilesToRender),
      tableColumnConfig,
      filterConfig,
    )
    if (profileFilters) {
      updatedProfileData = filterService.applyCustomFilters(updatedProfileData, profileFilters)
    }
    setProfileData(updatedProfileData)
    setTableReloadTrigger(Date.now())
  }

  const searchHandler = (e: React.ChangeEvent<{ value: string }>) => {
    const newSearchString = e.target.value
    setSearchString(newSearchString)
  }

  const selectProfile = async (rowItem: Record<string, any>) => {
    try {
      sectionHeaderRef.current.disableSearch()
      let targetProfileData = profileData.find((x: ProfileCore) => x.id === rowItem.id)
      if (!targetProfileData) {
        return
      }
      const { id } = targetProfileData
      const profileEntity = profileRepo.getProfileEntity(id)
      if (!profileEntity) {
        throw { code: MissingProfile }
      }
      const profilePk = profileEntity.getPk()
      await loadProfile(profilePk, true)
    } catch (error) {
      errorHandler(error)
    }
  }

  const loadProfile = async (profilePk: ProfilePk, navigateToDataManager: boolean = false) => {
    try {
      setModalState({
        ...initialModalState,
        loadingModalOpen: true,
        loadingModalMessage: "Refreshing profile data...",
      })
      const authCredentials = {
        username: userIdPassport,
        password,
      }
      await profileService.refreshProfile(selectedAssociation, profileRepo, authCredentials, profilePk)
      const refreshedProfileEntity = profileRepo.getProfileEntity(profilePk)
      if (!refreshedProfileEntity) {
        throw { code: MissingProfile }
      }
      const { name, surname } = refreshedProfileEntity.getGeneralData()
      const profilePic = await profileService.getProfilePic(profilePk)
      const iamEntity = userRepo.getCurrentUserEntity().getAssocationIamEntity(selectedAssociation)
      if (!iamEntity) {
        throw { code: Unauthorised }
      }
      const profileNavMenuAccess = configService.generateProfileNavMenuConfig(
        iamEntity,
        selectedEmploymentStatus as EmploymentStatus,
      )
      updateState({
        profilePic,
        profile: refreshedProfileEntity,
        isReadOnly: false,
        dataCaptureMode: DataCaptureMode.STANDARD,
        activeDocumentPortalSection: DocumentCategories.RECRUITMENT_AND_ONBOARDING,
        processMode:
          selectedEmploymentStatus === "EMPLOYEE" ? ProcessModes.GENERAL_EMPLOYEE : ProcessModes.GENERAL_CANDIDATE,
        sourceData: cloneDeep(refreshedProfileEntity.getGeneralData()),
        profileNavMenuAccess,
      })
      const sideMenuComponents = (
        <ProfileNavMenu
          ref={profileViewingOptionsRef}
          profilePic={profilePic}
          name={`${name} ${surname}`}
          idPassport={userIdPassport}
          profilePk={profilePk}
          onClick={(screen) => sideMenuHandler(screen)}
          currentScreen={currentScreen}
          accessFlags={profileNavMenuAccess}
          key={userIdPassport}
        />
      )
      setSideMenuComponents(sideMenuComponents)
      setSideMenuVisible(true)
      closeModals()
      if (navigateToDataManager && profileNavMenuAccess.hasDataManagerAccess) {
        sideMenuHandler(UiCategoryEnum.DATA_MANAGER)
      }
    } catch (error) {
      errorHandler(error)
    }
  }

  const uploadConfig = async (classification: string, file: File, selectedAssociation: AssociationId) => {
    const validateFile = (classification: string, fileName: string, selectedAssociation: AssociationId) => {
      const classificationConfigTypeMap = {
        "PROCESS CONFIG": ConfigTypeEnum.PROCESS_CONFIG,
        "FIELD CONFIG": ConfigTypeEnum.FIELD_CONFIG,
        "ORGANISATION CONFIG": ConfigTypeEnum.ORGANISATION_CONFIG,
        "ALL DATA FLOWS": ConfigTypeEnum.ALL_DATA_FLOWS,
      } as Record<string, ConfigTypeEnum>

      const configType = classificationConfigTypeMap[classification]
      if (!configType) {
        throw { code: InvalidConfigType }
      }
      const normalisedFileName = toUpperCaseCustom(addUnderScores(fileName))
      const normalisedAssociation = toUpperCaseCustom(addUnderScores(selectedAssociation))
      const normalisedConfigType = `${toUpperCaseCustom(addUnderScores(configType))}.JSON`
      if (!normalisedFileName.startsWith(normalisedAssociation) || !normalisedFileName.endsWith(normalisedConfigType)) {
        throw { code: InvalidFileName }
      }
      return configType
    }
    try {
      const configType = validateFile(classification, file.name, selectedAssociation)

      setModalState({
        ...initialModalState,
        loadingModalOpen: true,
        loadingModalMessage: "Uploading config...",
      })
      const authCredentials = { username: userIdPassport, password }
      const config = await file.text()
      const jsonConfig = JSON.parse(config)
      const invalidations = await configService.uploadConfig(
        selectedAssociation,
        configType,
        jsonConfig,
        authCredentials,
      )
      if (invalidations && invalidations.length) {
        setModalState({
          ...initialModalState,
          warningModalOpen: true,
          warningModalHeader: "Invalidations Exist",
          warningModalMessage: "There are some problems with your config. A summary is being downloaded.",
        })
        const csvData = jsonToCsv(invalidations)
        packageDataAndDownloadFile(csvData, `INVALIDATIONS - ${classification}.csv`, "text/csv")
        return
      }
      setModalState({
        ...initialModalState,
        warningModalOpen: true,
        warningModalHeader: "Success",
        warningModalMessage: "The config was successfully updated",
      })
    } catch (error) {
      errorHandler(error)
    }
  }

  const sideMenuHandler = (screen: UiCategoryEnum) => {
    if (completedScreens.includes(screen)) {
      updateState({ currentScreen: screen, previousScreen: UiCategoryEnum.PROFILES })
      // pass current custom filter state to the next screen
      props.history.push(`/people/${toLowerCaseCustom(screen.split(" ").join(""))}`, {
        prevFilterState: filters,
      })
    } else {
      props.history.push("/comingsoon")
    }
  }

  const buidlToolbarConfig = (navMenuAccess: NavMenuAccess) => {
    const config = [
      {
        iconPath: filters.length > 0 ? mdiFilter : mdiFilterOutline,
        iconColor: ColorPalette.PRIMARY_BLUE,
        onClick: () => setFilterModalOpen(true),
        label: "FILTER",
        title: "Set custom filters on table data",
        count: filters.length,
      },
      {
        iconPath: mdiRefresh,
        iconColor: ColorPalette.PRIMARY_BLUE,
        onClick: () => downloadAssociationProfiles(true, tableColumnConfig, filterConfig, filters),
        label: "RELOAD",
        title: "Clear cache and refetch data",
      },
    ] as ActionButtonType[]
    if (navMenuAccess.hasConfigUploadAccess) {
      config.unshift({
        iconPath: mdiUpload,
        iconColor: ColorPalette.PRIMARY_BLUE,
        onClick: () =>
          setModalState({
            ...initialModalState,
            configUploadModalOpen: true,
          }),
        label: "UPLOAD CONFIG",
        title: "Upload a config file to update the table columns",
      })
    }
    return config
  }

  const errorHandler = (error: any): void => {
    let header = "Warning"
    let warningModalMessage = ""

    switch (error.code) {
      case Unauthorised:
        header = "Unauthorised"
        warningModalMessage = "You do not have permission to perform this action."
        break
      case MissingProfile:
        warningModalMessage = "There was a problem accessing this profile. Refresh the browser and try again."
        break
      case InvalidFileName:
        header = "Invalid File Name"
        warningModalMessage =
          "The file name is invalid. Ensure the file name follows the format: [ASSOCIATION]_[CONFIG TYPE].json"
        break
      case InvalidConfigType:
        header = "Invalid Config Type"
        warningModalMessage =
          "The config type is invalid. Ensure the config type is one of the following: PROCESS CONFIG, FIELD CONFIG, ORGANISATION CONFIG, ALL DATA FLOWS."
        break
      default:
        if ("message" in error) {
          warningModalMessage = `The following error message was returned: \n\n 
            ${error.message}.\n\n Contact tech support to report the issue.`
        } else {
          warningModalMessage =
            "We encountered a problem. Refresh the page and try again. If you are still unsuccessful, contact tech support for assistance"
        }
        break
    }
    setModalState({
      ...initialModalState,
      warningModalOpen: true,
      warningModalHeader: header,
      warningModalMessage,
    })
  }

  const { hasEmployeesAccess, hasCandidatesAccess } = navMenuAccess
  const {
    loadingModalOpen,
    loadingModalMessage,
    loadingModalAuxMessage,
    configUploadModalOpen,
    profileIntegrationWarningModalOpen,
  } = modalState

  const toolbarActionButtons: ActionButtonType[] = buidlToolbarConfig(navMenuAccess)
  const tableData = tableService.applySearch(searchString, profileData)
  const tableColumnDef = tableService.convertPfColumnConfigToGridColDef(tableColumnConfig)

  let fileSelectionModal = null
  if (configUploadModalOpen) {
    fileSelectionModal = (
      <FileSelectionModal
        open={true}
        title={"File Selection"}
        targetFileFormat={`${FileFormat.json}`}
        instructionMessage="Select a config file to upload"
        classificationLabel={"CONFIG TYPE"}
        fileClassificationOptions={["ALL DATA FLOWS", "FIELD CONFIG", "ORGANISATION CONFIG", "PROCESS CONFIG"]}
        defaultClassification="ALL DATA FLOWS"
        onCancel={() => closeModals()}
        onSubmit={(file, classification) => uploadConfig(classification, file, selectedAssociation)}
      />
    )
  }

  let profileIntegrationWarningModal = null
  if (profileIntegrationWarningModalOpen) {
    profileIntegrationWarningModal = (
      <ProfileIntegrationWarningModal
        data={integrationWarnings}
        onRowClick={(profilePk: ProfilePk) => loadProfile(profilePk, true)}
        onClose={() => closeModals()}
        onResolvedClick={(selectedProfilePks) =>
          markIntegrationWarningResolved(selectedProfilePks, selectedAssociation, userIdPassport, password)
        }
        onProfileSelect={() => {}}
      />
    )
  }

  return (
    <div style={{ ...styles.container }}>
      <NavBar
        match={props.match}
        location={props.location}
        history={props.history}
        selectedProfilePks={selectedProfilePks}
      />
      <SectionHeader
        ref={sectionHeaderRef}
        style={styles.sectionHeader}
        searchString={searchString}
        textHandler={searchHandler}
        onClick={() => {
          setSideMenuVisible(false)
        }}
        key={`sectionHeader_${selectedAssociation}`}>
        {titleCase(`${currentScreen}`)}
      </SectionHeader>

      <div style={styles.contentContainer}>
        <SideMenu visible={sideMenuVisible} menuComponents={sideMenuComponents} />
        <div
          style={{
            ...styles.rightSide,
            paddingInline: sideMenuVisible ? "max(2em, 2%)" : "min(3em, 3%)",
          }}>
          <Toolbar actionButtons={toolbarActionButtons} key={`actionToolbar_${toolbarActionButtons.length}`} />
          <div style={styles.rightSideContent} ref={primaryTableRef}>
            <Table
              rows={tableData}
              columns={tableColumnDef}
              tableHeight={tableHeight}
              rowSelection={true}
              onRowClick={(rowData: Record<string, any>) => selectProfile(rowData)}
              onRowSelection={setSelectedProfilePks}
              key={`dataTable_${selectedAssociation}_${tableReloadTrigger}_${tableColumnConfig.length}`}
            />
          </div>
        </div>
      </div>

      {fileSelectionModal}
      {profileIntegrationWarningModal}

      <Modal open={filterModalOpen} title="" contentContainerStyle={{ padding: 0 }}>
        <FieldFilter
          filtersOnLoad={filters}
          filterConfig={filterConfig}
          onDoneClick={(updatedFilters) => {
            setFilters(updatedFilters)
            updateProfileRenderData(tableColumnConfig, filterConfig, updatedFilters)
            setFilterModalOpen(false)
          }}
        />
      </Modal>

      <AlertModalOneButton
        // TODO: There should only be one AlertModalOneButton component per screen with the required headers, body, and functions being set accordingly by a handler.
        open={
          (!hasEmployeesAccess && selectedEmploymentStatus === EmploymentStatus.EMPLOYEE) ||
          (!hasCandidatesAccess && selectedEmploymentStatus === EmploymentStatus.CANDIDATE)
        }
        header="Not Authorised"
        body="You don't have permission to view employee/candidate info."
        buttonLabel="Ok"
        opaqueBackground={true}
        onClick={() => props.history.goBack()}
      />
      <AlertModalOneButton
        open={modalState.warningModalOpen}
        header={modalState.warningModalHeader}
        body={modalState.warningModalMessage}
        buttonLabel={"Ok"}
        onClick={() => closeModals()}
      />
      <LoadingModal
        open={loadingModalOpen}
        loadingMessage={loadingModalMessage}
        auxilliaryMessage={loadingModalAuxMessage}
      />
    </div>
  )
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    backgroundImage: `linear-gradient(to bottom, ${ColorPalette.SCREEN_TOP_GRADIENT}, ${ColorPalette.SCREEN_BOTTOM_GRADIENT})`,
    height: "100vh",
  },
  sectionHeader: {
    margin: "3.5% auto 0.5%",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "auto",
  },
  rightSide: {
    display: "flex",
    flexDirection: "column" as "column",
    width: "100%",
    overflow: "hidden",
  },
  rightSideContent: {
    boxShadow: "0px -1px 8px rgba(60,60,60, 0.1)",
    display: "flex",
    flex: 1,
    backgroundColor: ColorPalette.CARD_WHITE,
  },
  customFilterButton: {
    height: "100%",
    outline: 0,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    zIndex: 300,
  },
}

export default Radium(Profiles)
